import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService as api } from '../services/api';
import { BILLING_UPGRADE_PATH } from '../constants/urls';

// Types
export interface ResourceLimitInfo {
  resource_type: string
  user_tier: string
  limit_status: {
    status: 'unlimited' | 'limited'
    current_count: number
    limit: number | null
    percentage_used: number
    warning_level: 'info' | 'warning' | 'critical' | 'exceeded' | null
    remaining: number
  }
  upgrade_suggestion: string | null
  upgrade_url: string | null
}

export interface FeatureAccessResult {
  has_access: boolean
  feature: string
  tier?: string
  current_tier?: string
  required_tier?: string
  upgrade_benefits?: any
  message: string
}

export interface TierInfo {
  current_tier: {
    tier: string;
    name: string;
    price_monthly: number;
    limits: {
      max_memories: number;
      max_spaces: number;
      monthly_requests: number;
      memory_expiration_days: number | null;
    };
    features: {
      advanced_search: boolean;
      api_access: boolean;
      priority_support: boolean;
      custom_integrations: boolean;
    };
  };
  features: Record<string, boolean>;
  limits: {
    max_memories: number;
    max_spaces: number;
    monthly_requests: number;
    memory_expiration_days: number | null;
  };
  api_usage: {
    current_month: number;
    percentage: number;
  };
  upgrade_opportunities?: Array<{
    tier: string;
    benefit: string;
    priority: number;
  }>;
}

interface TierContextType {
  tierInfo: TierInfo | null;
  loading: boolean;
  error: string | null;
  /** Resolves to API tier id after fetch, or null if skipped/failed. */
  refreshTierInfo: () => Promise<string | null>;
  getResourceLimits: (resourceType: string, currentCount?: number) => Promise<ResourceLimitInfo | null>;
  getCurrentTier: () => string;
  getNextTier: () => string | null;
  canAccess: (feature: string) => boolean;
  requiresUpgrade: (feature: string) => boolean;
  checkFeatureAccess: (feature: string) => Promise<FeatureAccessResult>;
  getUsageWarning: (resourceType: string, currentCount: number, limit: number | null) => string | null;
  formatLimit: (limit: number | null) => string;
  isApproachingLimit: (resourceType: string) => boolean;
  getUpgradeBenefits: () => any;
}

// Context
const TierContext = createContext<TierContextType | undefined>(undefined);

// Provider Props
interface TierProviderProps {
  children: ReactNode;
}

/** Maps GET /tier/usage-analytics payload to TierInfo.api_usage (current_month + percentage). */
function apiUsageFromTierUsageResponse(
  usageResponse: {
    analytics?: {
      error?: string;
      period_usage?: { monthly?: number };
    } | null;
    usage_percentages?: { monthly?: number };
  },
  monthlyRequestLimit: number | null | undefined
): { current_month: number; percentage: number } {
  const analytics = usageResponse.analytics;
  if (!analytics || typeof analytics !== 'object' || 'error' in analytics) {
    return { current_month: 0, percentage: 0 };
  }
  const monthly = analytics.period_usage?.monthly;
  const current_month =
    typeof monthly === 'number' && !Number.isNaN(monthly) ? monthly : 0;

  const backendPct = usageResponse.usage_percentages?.monthly;
  if (typeof backendPct === 'number' && !Number.isNaN(backendPct)) {
    return {
      current_month,
      percentage: Math.min(100, Math.round(backendPct)),
    };
  }

  if (typeof monthlyRequestLimit === 'number' && monthlyRequestLimit > 0) {
    return {
      current_month,
      percentage: Math.min(
        100,
        Math.round((current_month / monthlyRequestLimit) * 100)
      ),
    };
  }
  return { current_month, percentage: 0 };
}

// Provider Component
export function TierProvider({ children }: TierProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [tierInfo, setTierInfo] = useState<TierInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);
  const hasLoadedForCurrentAuth = useRef(false);

  // Load tier information
  const loadTierInfo = useCallback(async (forceRefresh = false): Promise<string | null> => {
    if (!isAuthenticated || authLoading) {
      return null;
    }

    // Prevent duplicate loads
    if (loadingRef.current && !forceRefresh) {
      return null;
    }

    // If we already loaded for this auth session and not forcing refresh, skip
    if (!forceRefresh && hasLoadedForCurrentAuth.current) {
      return null;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Load tier and usage data in parallel
      const [tierResponse, usageResponse] = await Promise.all([
        api.get('/tier/tier'),
        api.get('/tier/usage-analytics')
      ]);

      // Combine the data
      const combinedTierInfo: TierInfo = {
        current_tier: tierResponse,
        features: tierResponse.features,
        limits: tierResponse.limits,
        api_usage: apiUsageFromTierUsageResponse(
          usageResponse,
          tierResponse.limits?.monthly_requests
        ),
        upgrade_opportunities: usageResponse.recommendations || []
      };

      setTierInfo(combinedTierInfo);
      hasLoadedForCurrentAuth.current = true;

      const tierId =
        typeof tierResponse?.tier === 'string' ? tierResponse.tier : 'free';
      return tierId;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load tier information';
      console.error('❌ TierProvider: Error loading tier info:', errorMsg);
      setError(errorMsg);
      setTierInfo(null);
      return null;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [isAuthenticated, authLoading]);

  // Load on auth change
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // Only load if we haven't loaded for this auth session yet
      if (!hasLoadedForCurrentAuth.current) {
        loadTierInfo();
      }
    } else if (!isAuthenticated && !authLoading) {
      // Clear tier info on logout
      setTierInfo(null);
      setLoading(false);
      setError(null);
      hasLoadedForCurrentAuth.current = false;
    }
  }, [isAuthenticated, authLoading, loadTierInfo]);

  // Refresh function for manual updates
  const refreshTierInfo = useCallback(async (): Promise<string | null> => {
    hasLoadedForCurrentAuth.current = false;
    return loadTierInfo(true);
  }, [loadTierInfo]);

  // Get resource limit information
  const getResourceLimits = useCallback(async (
    resourceType: string, 
    currentCount: number = 0
  ): Promise<ResourceLimitInfo | null> => {
    try {
      // Early return if still loading or no tier info
      if (loading || !tierInfo) {
        return null;
      }

      const limit = tierInfo.limits[`max_${resourceType}` as keyof typeof tierInfo.limits] as number | null;
      const isUnlimited = limit === null;
      const percentage = isUnlimited ? 0 : Math.round((currentCount / limit!) * 100);
      
      let warningLevel: 'info' | 'warning' | 'critical' | 'exceeded' | null = null;
      if (!isUnlimited) {
        if (currentCount >= limit!) {
          warningLevel = 'exceeded';
        } else if (percentage >= 90) {
          warningLevel = 'critical';
        } else if (percentage >= 75) {
          warningLevel = 'warning';
        } else if (percentage >= 50) {
          warningLevel = 'info';
        }
      }

      return {
        resource_type: resourceType,
        user_tier: tierInfo.current_tier.tier,
        limit_status: {
          status: isUnlimited ? 'unlimited' : 'limited',
          current_count: currentCount,
          limit: limit,
          percentage_used: percentage,
          warning_level: warningLevel,
          remaining: isUnlimited ? Infinity : Math.max(0, limit! - currentCount)
        },
        upgrade_suggestion: warningLevel === 'exceeded' || warningLevel === 'critical' ? 
          `Consider upgrading for more ${resourceType}` : null,
        upgrade_url: warningLevel === 'exceeded' || warningLevel === 'critical' ? 
          BILLING_UPGRADE_PATH : null
      };
    } catch (err) {
      console.error(`TierProvider: Failed to get resource limits for ${resourceType}:`, err);
      return null;
    }
  }, [loading, tierInfo]);

  // Helper functions  
  const getCurrentTier = (): string => {
    return tierInfo?.current_tier?.tier || 'free';
  };

  const getNextTier = (): string | null => {
    const current = getCurrentTier();
    const tierHierarchy = ['free', 'starter', 'pro', 'enterprise'];
    const currentIndex = tierHierarchy.indexOf(current);
    
    if (currentIndex === -1 || currentIndex >= tierHierarchy.length - 1) {
      return null;
    }
    
    return tierHierarchy[currentIndex + 1];
  };

  // Check if user has access to a specific feature
  const canAccess = (feature: string): boolean => {
    if (!tierInfo) return false;
    return tierInfo.features[feature] === true;
  };

  // Check if feature requires upgrade
  const requiresUpgrade = (feature: string): boolean => {
    if (!tierInfo) return false;
    return !tierInfo.features[feature];
  };

  // Check specific feature access with detailed info
  const checkFeatureAccess = async (feature: string): Promise<FeatureAccessResult> => {
    try {
      const result = await api.get(`/tier/features/${feature}`);
      return result;
    } catch (err) {
      console.error(`Failed to check feature access for ${feature}:`, err);
      return {
        has_access: false,
        feature,
        message: 'Unable to check feature access'
      };
    }
  };

  // Format limit for display
  const formatLimit = (limit: number | null): string => {
    if (limit === null) return 'Unlimited';
    if (limit >= 1000000) return `${Math.floor(limit / 1000000)}M`;
    if (limit >= 1000) return `${Math.floor(limit / 1000)}K`;
    return limit.toString();
  };

  // Get warning message for resource usage
  const getUsageWarning = (
    resourceType: string, 
    currentCount: number, 
    limit: number | null
  ): string | null => {
    if (limit === null) return null; // Unlimited
    
    const percentage = (currentCount / limit) * 100;
    const remaining = Math.max(0, limit - currentCount);
    
    if (percentage >= 100) {
      return `You've reached your limit of ${formatLimit(limit)} ${resourceType}. Upgrade for more!`;
    } else if (percentage >= 90) {
      return `You're close to your ${resourceType} limit! Only ${remaining} remaining.`;
    } else if (percentage >= 75) {
      return `You've used ${Math.round(percentage)}% of your ${resourceType} allowance.`;
    }
    
    return null;
  };

  // Check if user is approaching limits
  const isApproachingLimit = (resourceType: string): boolean => {
    if (!tierInfo) return false;
    
    // For now, we'll use the tier info to determine this
    // In a full implementation, this would check current usage vs limits
    const limit = tierInfo.limits[`max_${resourceType}` as keyof typeof tierInfo.limits] as number | null;
    if (limit === null) return false; // Unlimited
    
    // This is a simplified check - in practice you'd need current counts
    return false;
  };

  // Get upgrade benefits for current user
  const getUpgradeBenefits = () => {
    return tierInfo?.upgrade_opportunities || [];
  };

  const value: TierContextType = {
    tierInfo,
    loading,
    error,
    refreshTierInfo,
    getResourceLimits,
    getCurrentTier,
    getNextTier,
    canAccess,
    requiresUpgrade,
    checkFeatureAccess,
    getUsageWarning,
    formatLimit,
    isApproachingLimit,
    getUpgradeBenefits
  };

  return (
    <TierContext.Provider value={value}>
      {children}
    </TierContext.Provider>
  );
}

// Hook to use tier context
export function useTier(): TierContextType {
  const context = useContext(TierContext);
  if (context === undefined) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
}

// Utility hooks for specific data
export function useApiLimits() {
  const { tierInfo } = useTier();
  
  return {
    monthlyLimit: tierInfo?.limits.monthly_requests || 0,
    currentUsage: tierInfo?.api_usage.current_month || 0,
    percentage: tierInfo?.api_usage.percentage || 0,
    isNearLimit: (tierInfo?.api_usage.percentage || 0) > 80
  };
}

// Convenience hooks for specific features
export function useMemoryLimits() {
  const { getResourceLimits, tierInfo, formatLimit } = useTier();
  
  const checkMemoryLimits = useCallback(async (currentCount: number) => {
    return await getResourceLimits('memories', currentCount);
  }, [getResourceLimits]);
  
  const hasUnlimitedMemories = tierInfo?.limits.max_memories === null;
  const memoryLimit = tierInfo?.limits.max_memories ?? null;
  
  return {
    checkMemoryLimits,
    hasUnlimitedMemories,
    memoryLimit,
    formattedLimit: memoryLimit !== null ? formatLimit(memoryLimit) : 'Unlimited'
  };
}

export function useSpaceLimits() {
  const { getResourceLimits, tierInfo, formatLimit } = useTier();
  
  const checkSpaceLimits = useCallback(async (currentCount: number) => {
    return await getResourceLimits('spaces', currentCount);
  }, [getResourceLimits]);
  
  const spaceLimit = tierInfo?.limits.max_spaces ?? null;
  
  return {
    checkSpaceLimits,
    spaceLimit,
    formattedLimit: spaceLimit !== null ? formatLimit(spaceLimit) : 'Unknown'
  };
}
